import React from "react";
import { LinkOutlined } from "@ant-design/icons";
import styled from "styled-components";
import type { EngagementTermsCustomizationField } from "@/api/engagementTerms";
import { Icon } from "@/shared/components/Icon/Icon";

export const TermsSection = styled.section`
	padding: 32px 0;
	border-bottom: 1px solid #e6e6e6;

	&:last-child {
		border-bottom: 0;
	}
`;

export const SectionHeader = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 8px;
`;

export const SectionCopy = styled.div`
	min-width: 0;
	flex: 1;
`;

export const SectionTitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

export const SectionTitle = styled.h2`
	margin: 0;
	font-size: 16px;
	font-weight: 600;
	color: #222;
`;

export const SectionDescription = styled.div`
	margin-top: 8px;
	font-size: 12px;
	color: rgba(34, 34, 34, 0.6);
`;

export const Fields = styled.div<{ $columns?: number }>`
	display: grid;
	grid-template-columns: repeat(${(props) => props.$columns || 2}, minmax(0, 1fr));
	gap: 24px;
	margin-top: 24px;
`;

export const Field = styled.label<{ $invalid?: boolean }>`
	display: block;
	min-width: 0;
	color: #707070;
	font-size: 14px;

	.ant-input,
	.app-date-picker,
	.ant-select-selector {
		border-color: ${(props) => (props.$invalid ? "#f72586" : "#cdd4f6")} !important;
		border-radius: 8px !important;
		box-shadow: none !important;
		font-size: 16px !important;
		color: #171717 !important;
	}

	.ant-input {
		height: 41px;
		margin-top: 2px;
		padding: 8px 42px 8px 12px;
	}

	.app-date-picker {
		width: 100%;
		height: 41px;
		margin-top: 2px;
		padding: 8px 12px;
	}

	.app-date-picker input {
		font-size: 16px;
		color: #171717;
	}

	.ant-select {
		width: 100%;
		height: 41px;
		margin-top: 2px;
	}

	.ant-select-selector {
		height: 41px !important;
		display: flex !important;
		align-items: center !important;
		padding: 0 12px !important;
	}

	.ant-select-selection-item {
		line-height: normal !important;
	}

	.ant-select-arrow {
		top: 50%;
		margin-top: 0;
		transform: translateY(-50%);
	}
`;

export const FieldLabelRow = styled.span`
	display: flex;
	min-height: 22px;
	align-items: center;
	gap: 6px;
`;

export const RequiredMark = styled.span.attrs({ className: "cet-required-mark" })`
	margin-left: 3px;
	color: #f72586;
`;

export const InlineError = styled.span`
	display: block;
	margin-top: 4px;
	color: #d80027;
	font-size: 11px;
	line-height: 14px;
`;

const SyncedChip = styled.span<{ $compact?: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: ${(props) => (props.$compact ? "3px" : "4px")};
	min-height: ${(props) => (props.$compact ? "18px" : "22px")};
	box-sizing: border-box;
	padding: ${(props) => (props.$compact ? "1px 6px" : "3px 8px")};
	border-radius: 24px;
	background: #e7ddfe;
	color: #3a0ca3;
	font-size: ${(props) => (props.$compact ? "10px" : "12px")};
	font-weight: 500;
	line-height: 1;
`;

const CustomChip = styled(SyncedChip)`
	background: #fff0cf;
	color: #bd7b00;
`;

export const TermsStatusBadge: React.FC<{
	field: EngagementTermsCustomizationField;
	customizedFields: EngagementTermsCustomizationField[];
	compact?: boolean;
}> = ({ field, customizedFields, compact = false }) =>
	customizedFields.includes(field) ? (
		<CustomChip $compact={compact}>
			<Icon src="/icon/cet-custom.svg" size={compact ? 12 : 15} /> Custom
		</CustomChip>
	) : (
		<SyncedChip $compact={compact}>
			<LinkOutlined /> Synced
		</SyncedChip>
	);
